/**
 * CreateTripV2 — modular trip wizard (thin UI orchestrator).
 *
 * Legacy CreateTrip.js is intentionally left untouched as a rollback fallback.
 * Switch routes.js imports between CreateTrip and CreateTripV2 if needed.
 */
import { Alert, Card, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Snackbar } from '@mui/material';
import PearlLogoLoader from 'components/PearlLogoLoader';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import PaymentTemplateModal from 'layouts/Travel/payment-templates/PaymentTemplateModal';
import HighSeasonSurchargeModal from 'layouts/Travel/hss/HighSeasonSurchargeModal';
import NewPackageComponentModal from './NewPackageComponentModal';
import TripWizardStepper from './wizard/stepper/TripWizardStepper';
import TripStatusSelector from './wizard/components/TripStatusSelector';
import { renderWizardStep } from './wizard/steps';
import SearchExistingComponentsModal from './SearchExistingComponentsModal';
import useCreateTripWizard from './wizard/hooks/useCreateTripWizard';

/** Set true to show draft/active status chip in the wizard header. */
const SHOW_HEADER_TRIP_STATUS = false;

const CreateTripV2 = () => {
    const {
        pageTitle,
        wizard_steps,
        activeStep,
        completedSteps,
        isEditMode,
        apiError,
        isSaving,
        isContinuing,
        isTripLoading,
        validationSnackbar,
        draftSnackbar,
        cancelDialogOpen,
        publishDialogOpen,
        formData,
        templateModalOpen,
        editingTemplate,
        hssModalOpen,
        isUpdatingHss,
        componentModalOpen,
        handleStepClick,
        handleCancelTrip,
        handleCancelDialogClose,
        handleConfirmCancelTrip,
        handlePublishDialogClose,
        handlePublishDecline,
        handlePublishConfirm,
        handleContinue,
        handleSaveDraft,
        handleBack,
        handleSaveTemplate,
        closeHssModal,
        handleSaveHssTemplate,
        handleSaveNewComponent,
        closeComponentModal,
        searchComponentModalOpen,
        closeSearchComponentModal,
        handleImportExistingComponent,
        isImportingComponent,
        importComponentContextRef,
        setTemplateModalOpen,
        setValidationSnackbar,
        setDraftSnackbar,
        stepProps,
        tripStatus,
        setTripStatus,
    } = useCreateTripWizard();

    const isReviewStep = wizard_steps[activeStep] === 'Review';

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                sx={{ overflow: 'visible' }}
                            >
                                <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                                    <MDBox>
                                        <MDTypography variant="h5" color="white">
                                            {pageTitle}
                                        </MDTypography>
                                        <MDTypography variant="caption" color="white" opacity={0.9} display="block" mt={0.5}>
                                            Step {activeStep + 1} of {wizard_steps.length} — {wizard_steps[activeStep]}
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox display="flex" alignItems="center" gap={1.5} flexShrink={0} flexWrap="wrap">
                                        {SHOW_HEADER_TRIP_STATUS && !isReviewStep && (
                                            <TripStatusSelector
                                                value={tripStatus}
                                                onChange={setTripStatus}
                                                disabled={isSaving || isContinuing}
                                            />
                                        )}
                                        {isSaving && (
                                            <>
                                                <PearlLogoLoader size="xs" showMessage={false} message="" />
                                                <MDTypography variant="caption" color="white">
                                                    Saving progress...
                                                </MDTypography>
                                            </>
                                        )}
                                        <MDButton variant="outlined" color="white" size="small" onClick={handleCancelTrip}>
                                            Cancel
                                        </MDButton>
                                    </MDBox>
                                </MDBox>

                                <TripWizardStepper
                                    steps={wizard_steps}
                                    activeStep={activeStep}
                                    completedSteps={completedSteps}
                                    onStepClick={handleStepClick}
                                    allStepsClickable={isEditMode}
                                />
                            </MDBox>

                            <MDBox p={3}>
                                {apiError && (
                                    <Alert severity="error" sx={{ mb: 3 }}>
                                        {apiError}
                                    </Alert>
                                )}

                                {isTripLoading ? (
                                    <MDBox py={8} display="flex" justifyContent="center">
                                        <PearlLogoLoader size="md" message="Loading Destination..." />
                                    </MDBox>
                                ) : (
                                    renderWizardStep(activeStep, stepProps)
                                )}

                                <PaymentTemplateModal
                                    open={templateModalOpen}
                                    onClose={() => setTemplateModalOpen(false)}
                                    onSave={handleSaveTemplate}
                                    template={editingTemplate}
                                />

                                <HighSeasonSurchargeModal
                                    open={hssModalOpen}
                                    onClose={closeHssModal}
                                    onSave={handleSaveHssTemplate}
                                    template={null}
                                    isSaving={isUpdatingHss}
                                />

                                <NewPackageComponentModal
                                    open={componentModalOpen}
                                    onClose={closeComponentModal}
                                    onSave={handleSaveNewComponent}
                                    defaultCurrency={
                                        formData?.pricing?.currency ||
                                        formData?.packages?.currency ||
                                        ''
                                    }
                                />

                                <SearchExistingComponentsModal
                                    open={searchComponentModalOpen}
                                    onClose={closeSearchComponentModal}
                                    tripId={stepProps.tripId}
                                    onImport={handleImportExistingComponent}
                                    attachContext={importComponentContextRef.current}
                                    isImporting={isImportingComponent}
                                />

                                <MDBox
                                    mt={3}
                                    pt={2}
                                    display="flex"
                                    justifyContent="space-between"
                                    gap={2}
                                    sx={{ borderTop: 1, borderColor: 'divider' }}
                                >
                                    {activeStep > 0 && (
                                        <MDButton
                                            color="secondary"
                                            variant="outlined"
                                            disabled={isSaving || isContinuing}
                                            onClick={handleBack}
                                        >
                                            Back
                                        </MDButton>
                                    )}

                                    <MDBox display="flex" gap={1} ml="auto">
                                        <MDButton
                                            color="dark"
                                            variant="outlined"
                                            disabled={isSaving || isContinuing}
                                            onClick={() => handleSaveDraft()}
                                            startIcon={<SaveOutlinedIcon />}
                                        >
                                            Save Draft
                                        </MDButton>
                                        <MDButton
                                            variant="gradient"
                                            color="info"
                                            disabled={isSaving || isContinuing}
                                            onClick={() => handleContinue()}
                                        >
                                            {isContinuing ? (
                                                <MDBox display="flex" alignItems="center" gap={1}>
                                                    <CircularProgress size={14} sx={{ color: '#fff !important' }} />
                                                    <MDTypography variant="button" sx={{ color: '#fff !important' }}>
                                                        Loading...
                                                    </MDTypography>
                                                </MDBox>
                                            ) : (
                                                <MDBox display="flex" alignItems="center" gap={1}>
                                                    <MDTypography variant="button" sx={{ color: '#fff !important' }}>
                                                        {isReviewStep ? 'Save and Publish' : 'Continue'}
                                                    </MDTypography>
                                                    {!isReviewStep && (
                                                        <ArrowForwardIcon sx={{ color: '#fff !important' }} />
                                                    )}
                                                </MDBox>
                                            )}
                                        </MDButton>
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <Snackbar
                open={validationSnackbar.open}
                autoHideDuration={5000}
                onClose={() => setValidationSnackbar({ open: false, message: '' })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity="error"
                    onClose={() => setValidationSnackbar({ open: false, message: '' })}
                >
                    {validationSnackbar.message}
                </Alert>
            </Snackbar>
            <Snackbar
                open={draftSnackbar.open}
                autoHideDuration={4000}
                onClose={() => setDraftSnackbar({ open: false, message: '', severity: 'success' })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={draftSnackbar.severity}
                    onClose={() => setDraftSnackbar({ open: false, message: '', severity: 'success' })}
                >
                    {draftSnackbar.message}
                </Alert>
            </Snackbar>

            <Dialog
                open={cancelDialogOpen}
                onClose={handleCancelDialogClose}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Cancel trip creation?</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" color="text">
                        Unsaved progress will be lost. Are you sure you want to cancel?
                    </MDTypography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={handleCancelDialogClose}
                    >
                        Keep Editing
                    </MDButton>
                    <MDButton
                        variant="gradient"
                        color="error"
                        onClick={handleConfirmCancelTrip}
                    >
                        Yes, Cancel
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={publishDialogOpen}
                onClose={handlePublishDialogClose}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Publish this trip?</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" color="text">
                        Are you sure you want to publish it? The trip will be saved and set to Active.
                    </MDTypography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={handlePublishDecline}
                        disabled={isSaving || isContinuing}
                    >
                        No, Save Draft
                    </MDButton>
                    <MDButton
                        variant="gradient"
                        color="info"
                        onClick={handlePublishConfirm}
                        disabled={isSaving || isContinuing}
                    >
                        Yes, Publish
                    </MDButton>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
};

export default CreateTripV2;
